import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const UserForm = ({ user, companies, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    companyId: '',
    plantAccess: []
  });

  const [errors, setErrors] = useState({});
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        role: user?.role || '',
        companyId: user?.companyId || '',
        plantAccess: user?.plantAccess || []
      });
      if (user?.companyId) {
        const company = companies?.find(c => c?.id === user?.companyId);
        setSelectedCompany(company);
      }
    }
  }, [user, companies]);

  useEffect(() => {
    if (formData?.companyId) {
      const company = companies?.find(c => c?.id === formData?.companyId);
      setSelectedCompany(company);
    } else {
      setSelectedCompany(null);
    }
  }, [formData?.companyId, companies]);

  const roleOptions = [
    { value: 'Admin', label: 'Admin - Full system access' },
    { value: 'User', label: 'User - Limited plant access' }
  ];

  const companyOptions = companies?.map(c => ({
    value: c?.id,
    label: c?.name
  }));

  const plantOptions = selectedCompany?.plants?.map(p => ({
    value: p?.id,
    label: `${p?.name} - ${p?.location}`
  })) || [];

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData?.role) newErrors.role = 'Role is required';
    if (!formData?.companyId) newErrors.companyId = 'Company assignment is required';
    if (formData?.role === 'User' && formData?.plantAccess?.length === 0) {
      newErrors.plantAccess = 'At least one plant must be selected for regular users';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in">
      <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {user ? 'Edit User' : 'Create New User'}
              </h2>
              <p className="text-sm text-muted-foreground">Enter user details and permissions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted transition-colors duration-250 flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="John Doe"
              required
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              error={errors?.name}
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="john@example.com"
              required
              value={formData?.email}
              onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
              error={errors?.email}
            />
          </div>

          <Select
            label="Role"
            options={roleOptions}
            value={formData?.role}
            onChange={(value) => setFormData({ ...formData, role: value })}
            placeholder="Select role"
            required
            error={errors?.role}
            description="Admin users have full system access, regular users have limited plant-specific permissions"
          />

          <Select
            label="Assign Company"
            options={companyOptions}
            value={formData?.companyId}
            onChange={(value) => setFormData({ ...formData, companyId: value, plantAccess: [] })}
            placeholder="Select company"
            required
            error={errors?.companyId}
          />

          {formData?.role === 'User' && selectedCompany && (
            <div>
              <Select
                label="Plant Access"
                options={plantOptions}
                value={formData?.plantAccess}
                onChange={(value) => setFormData({ ...formData, plantAccess: value })}
                placeholder="Select plants"
                multiple
                required
                error={errors?.plantAccess}
                description="Select which plants this user can access"
              />
              {plantOptions?.length === 0 && (
                <p className="text-sm text-warning mt-2">
                  No plants available for this company. Add plants first.
                </p>
              )}
            </div>
          )}

          {formData?.role === 'Admin' && (
            <div className="bg-accent/10 rounded-lg p-4 flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Admin Access</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Admin users have full access to all plants and system features within the assigned company.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="default" fullWidth iconName="Save">
              {user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;